'use client';

import { useMemo, useState } from 'react';
import {
  ArrowUUpLeftIcon,
  CaretDownIcon,
  CheckCircleIcon,
  CheckIcon,
  CopySimpleIcon,
  ChecksIcon,
  ClockCountdownIcon,
  FlagIcon,
  ProhibitIcon,
  ReceiptIcon,
  TagIcon,
} from '@phosphor-icons/react';
import type { ReviewQueueItem } from '@/types';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CategoryIcon } from '@/components/CategoryIcon';
import { RuleCreationFields } from '@/components/categories/RuleCreationFields';
import { buildRuleAnchors, type RuleSaveOptions } from '@/components/categories/ruleAnchor';
import { categoryVisual } from '@/components/categories/categoryHelpers';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { cn } from '@/lib/utils';
import {
  REASON_LABEL,
  UNCATEGORIZED_CATEGORY_ID,
  isCategoryReviewReason,
  isDuplicateReviewReason,
  isRefundReviewReason,
  reviewItemKey,
} from './helpers';

const DEFAULT_SUBTITLE =
  'lançamentos novos, sem categoria ou com reembolsos a revisar — confirme para manter os números confiáveis';

interface ReviewQueueCardProps {
  items: ReviewQueueItem[];
  /** Real pendência count — the server caps `items` at its page size. */
  total: number;
  /** Header subtitle. Defaults to the category/refund copy; the investment-only host overrides it
   * so a queue of duplicate holdings is not described as "lançamentos sem categoria". */
  subtitle?: string;
  /** Accordion state — the card starts minimized; the KPI click expands it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Composite `reviewItemKey` of the busy row — two rows can share a transaction id. */
  confirmingId: string | null;
  confirmingAll: boolean;
  /** Category-row actions — the /budget host provides these; /transactions omits them. */
  onConfirm?: (item: ReviewQueueItem, options: RuleSaveOptions) => void;
  onAlter?: (item: ReviewQueueItem) => void;
  onConfirmAll?: () => void;
  /** Batch on a selection: confirm the chosen items as-is. */
  onConfirmSelected?: (ids: string[]) => Promise<void> | void;
  /** Batch on a selection: open the categorize drawer for the chosen items. */
  onAlterSelected?: (ids: string[]) => void;
  /** possible_refund: link to the proposed counterpart. /budget omits the refund trio. */
  onConfirmRefund?: (item: ReviewQueueItem) => void;
  /** Refund rows: open the link picker ("Alterar…" / "Vincular estorno…"). */
  onAlterRefund?: (item: ReviewQueueItem) => void;
  /** refund_not_arrived: keep the expense counting ("Manter como despesa"). */
  onKeepAsExpense?: (item: ReviewQueueItem) => void;
  /** possible_refund: reject the suggestion ("Não é reembolso") — the inflow stays income. */
  onDismissRefund?: (item: ReviewQueueItem) => void;
  /** duplicate_investment: the role IS the answer — "mesma aplicação" (MIRROR) or
   * "aplicações diferentes" (ADDITIVE) — so this carries it rather than merely accepting. */
  onConfirmDuplicate?: (item: ReviewQueueItem, role: 'MIRROR' | 'ADDITIVE') => void;
  /** duplicate_investment: dismiss the pair ("Não é duplicado"). */
  onDismissDuplicate?: (item: ReviewQueueItem) => void;
}

export function ReviewQueueCard({
  items,
  total,
  subtitle = DEFAULT_SUBTITLE,
  open,
  onOpenChange,
  confirmingId,
  confirmingAll,
  onConfirm,
  onAlter,
  onConfirmAll,
  onConfirmSelected,
  onAlterSelected,
  onConfirmRefund,
  onAlterRefund,
  onKeepAsExpense,
  onDismissRefund,
  onConfirmDuplicate,
  onDismissDuplicate,
}: ReviewQueueCardProps) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Confirming is irreversible per merchant (they become "known") — gate behind a modal.
  const [confirmTarget, setConfirmTarget] = useState<'all' | 'selected' | null>(null);

  // Refund rows resolve by linking, not by confirming a category — they stay out
  // of the selection batches entirely.
  const selectableItems = useMemo(
    () => items.filter((i) => isCategoryReviewReason(i.reason)),
    [items],
  );
  const selectedIds = useMemo(
    () => selectableItems.filter((i) => selected.has(i.id)).map((i) => i.id),
    [selectableItems, selected],
  );
  const allSelected = selectableItems.length > 0 && selectedIds.length === selectableItems.length;
  // Selection/confirm-all is the category-batch machinery — it needs category rows
  // AND a host that wired the batch handlers (a refund-only host does neither).
  const hasCategoryBatch =
    selectableItems.length > 0 && !!onConfirmAll && !!onConfirmSelected && !!onAlterSelected;

  const toggleOne = (id: string) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runConfirm = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (target === 'all') {
      onConfirmAll?.();
      setSelected(new Set());
    } else if (target === 'selected') {
      await onConfirmSelected?.(selectedIds);
      setSelected(new Set());
    }
  };

  const confirmCount = confirmTarget === 'all' ? total : selectedIds.length;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} asChild>
      <section
        id="revisar"
        className="overflow-hidden rounded-3xl border border-border-subtle bg-surface shadow-sm"
      >
        <div
          className={cn(
            'flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5',
            open && 'border-b border-border-subtle',
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-expanded={open}
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
            >
              <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-warning-surface text-warning-text">
                <FlagIcon size={14} weight="duotone" />
              </span>
              <span className="text-2xs font-semibold tracking-widest text-text-primary uppercase">
                A revisar
              </span>
              <span className="rounded-full bg-warning-surface px-2 py-0.5 font-mono text-xs font-semibold text-warning-text tabular-nums">
                {total}
              </span>
              <span className="hidden min-w-0 truncate text-xs text-text-tertiary md:inline">
                {subtitle}
              </span>
              <CaretDownIcon
                className={cn('ml-auto size-4 shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
              />
            </button>
          </CollapsibleTrigger>
          {open && hasCategoryBatch && (
            <div className="flex shrink-0 items-center gap-1.5">
              {selectedIds.length > 0 ? (
                <>
                  <Button
                    size="sm"
                    disabled={confirmingAll}
                    onClick={() => setConfirmTarget('selected')}
                  >
                    <CheckIcon className="size-4" /> Confirmar ({selectedIds.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={confirmingAll}
                    onClick={() => onAlterSelected?.(selectedIds)}
                  >
                    <TagIcon className="size-4" /> Alterar ({selectedIds.length})
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmTarget('all')}
                  disabled={confirmingAll || items.length === 0}
                >
                  <ChecksIcon className="size-4" /> Confirmar todos
                </Button>
              )}
            </div>
          )}
        </div>

        <CollapsibleContent>
          {items.length === 0 ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-text-tertiary sm:px-5">
              <CheckCircleIcon className="size-4 text-success" weight="duotone" />
              Tudo em dia — nenhum lançamento aguardando confirmação.
            </div>
          ) : (
            <>
              <div className="max-h-[32rem] overflow-y-auto overscroll-contain">
                {/* Sticky select-all bar (the transactions screen's sticky-header treatment). */}
                {hasCategoryBatch && (
                  <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-2.5 border-b border-border-subtle bg-surface/85 px-4 py-2 text-xs text-text-secondary backdrop-blur-xl sm:px-5">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand-primary"
                      checked={allSelected}
                      onChange={(e) =>
                        setSelected(e.target.checked ? new Set(selectableItems.map((i) => i.id)) : new Set())
                      }
                    />
                    {selectedIds.length > 0
                      ? `${selectedIds.length} ${selectedIds.length === 1 ? 'selecionado' : 'selecionados'}`
                      : 'Selecionar todos'}
                  </label>
                )}
                <div className="divide-y divide-border-subtle">
                  {items.map((item) => (
                    isDuplicateReviewReason(item.reason) ? (
                      <DuplicateReviewRow
                        key={reviewItemKey(item)}
                        item={item}
                        busy={confirmingId === reviewItemKey(item) || confirmingAll}
                        onConfirmDuplicate={onConfirmDuplicate}
                        onDismissDuplicate={onDismissDuplicate}
                      />
                    ) : isRefundReviewReason(item.reason) ? (
                      <RefundReviewRow
                        key={reviewItemKey(item)}
                        item={item}
                        busy={confirmingId === reviewItemKey(item) || confirmingAll}
                        onConfirmRefund={onConfirmRefund}
                        onAlterRefund={onAlterRefund}
                        onKeepAsExpense={onKeepAsExpense}
                        onDismissRefund={onDismissRefund}
                      />
                    ) : (
                      <ReviewQueueRow
                        key={reviewItemKey(item)}
                        item={item}
                        busy={confirmingId === reviewItemKey(item) || confirmingAll}
                        selected={selected.has(item.id)}
                        onToggleSelected={() => toggleOne(item.id)}
                        onConfirm={onConfirm}
                        onAlter={onAlter}
                      />
                    )
                  ))}
                </div>
              </div>
              {total > items.length && (
                <p className="border-t border-border-subtle bg-surface-sunken px-4 py-2 text-2xs text-text-tertiary sm:px-5">
                  Mostrando os {items.length} mais recentes de {total} — confirme ou categorize para
                  ver os demais.
                </p>
              )}
            </>
          )}
        </CollapsibleContent>

        <Dialog open={confirmTarget != null} onOpenChange={(o) => !o && setConfirmTarget(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>
                Confirmar {confirmCount === 1 ? '1 lançamento' : `${confirmCount} lançamentos`}?
              </DialogTitle>
              <DialogDescription>
                As categorias sugeridas ficam como estão e os estabelecimentos passam a ser
                conhecidos — estes lançamentos não voltam para a fila.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmTarget(null)}>
                Voltar
              </Button>
              <Button onClick={runConfirm}>
                <ChecksIcon className="size-4" /> Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </Collapsible>
  );
}

// The evidence behind a proposal, in plain pt-BR. The sync ships a machine token; showing it
// raw would be noise, so each maps to the reason a person would give.
const DUPLICATE_MATCH_EVIDENCE: Record<string, string> = {
  UNKNOWN_INSTITUTION_MATCHING_VALUES: 'saldos iguais',
  UNKNOWN_INSTITUTION_DIFFERENT_VALUES: 'saldos diferentes',
  UNKNOWN_INSTITUTION_SHARED_HISTORY: 'mesmas movimentações',
  UNKNOWN_INSTITUTION_DISJOINT_HISTORY: 'movimentações distintas',
  SAME_INSTITUTION_SAME_CONNECTION: 'mesma conexão',
  SAME_INSTITUTION_SECOND_CONNECTION: 'segunda conexão da instituição',
  SAME_INSTITUTION_SHARED_HISTORY: 'mesma instituição, mesmas movimentações',
  SAME_INSTITUTION_DISJOINT_HISTORY: 'mesma instituição, movimentações distintas',
  DIFFERENT_INSTITUTIONS_SHARED_HISTORY: 'mesmas movimentações em instituições diferentes',
  DIFFERENT_INSTITUTIONS: 'instituições diferentes',
};

// The lead-in has to match what the suggestion actually claims: a mirror is the same money seen
// twice ("já contabilizado"), but an additive is separate money added to the total — calling that
// "already counted" is simply false.
function duplicateLeadIn(matchReason: string | null, suggestedRole: string): string {
  if (matchReason === 'USER_SEPARATED') return 'Você separou esta posição de';
  if (suggestedRole === 'ADDITIVE') return 'Somado ao total, ao lado de';
  return 'Já contabilizado em';
}

function DuplicateReviewRow({
  item,
  busy,
  onConfirmDuplicate,
  onDismissDuplicate,
}: {
  item: ReviewQueueItem;
  busy: boolean;
  onConfirmDuplicate?: (item: ReviewQueueItem, role: 'MIRROR' | 'ADDITIVE') => void;
  onDismissDuplicate?: (item: ReviewQueueItem) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const suggestion = item.duplicateSuggestion ?? null;
  const evidence = suggestion && suggestion.matchReason !== 'USER_SEPARATED'
    ? DUPLICATE_MATCH_EVIDENCE[suggestion.matchReason ?? ''] ?? null
    : null;

  const handleMirror = () => onConfirmDuplicate?.(item, 'MIRROR');
  const handleAdditive = () => onConfirmDuplicate?.(item, 'ADDITIVE');
  const handleDismiss = () => onDismissDuplicate?.(item);

  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="size-4 shrink-0" aria-hidden />
        <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-info-border bg-info-surface text-info-text">
          <CopySimpleIcon className="size-4.5" weight="duotone" />
        </span>
        <div className="min-w-0 flex-1 basis-40">
          <p className="truncate text-sm font-semibold text-text-primary">{item.description}</p>
          <p className="text-xs text-text-tertiary">
            {item.merchant ?? 'Instituição não informada'}
            <span className="ml-2 inline-block rounded-full border border-info-border bg-info-surface px-1.5 py-px text-3xs font-medium whitespace-nowrap text-info-text">
              {REASON_LABEL[item.reason]}
            </span>
          </p>
        </div>
        <span className="font-mono text-sm font-medium text-text-primary tabular-nums">
          <MoneyValue text={formatCurrency(item.amount)} />
        </span>
        <div className="flex w-full flex-wrap items-center gap-2 pl-13 sm:w-auto sm:flex-nowrap sm:shrink-0 sm:pl-0">
          <Button
            size="sm"
            variant={suggestion?.suggestedRole === 'ADDITIVE' ? 'outline' : 'default'}
            disabled={busy || !onConfirmDuplicate}
            onClick={handleMirror}
          >
            <CheckIcon className="size-4" /> Mesma aplicação
          </Button>
          <Button
            size="sm"
            variant={suggestion?.suggestedRole === 'ADDITIVE' ? 'default' : 'outline'}
            disabled={busy || !onConfirmDuplicate}
            onClick={handleAdditive}
          >
            Aplicações diferentes
          </Button>
          <Button size="sm" variant="ghost" disabled={busy || !onDismissDuplicate} onClick={handleDismiss}>
            <ProhibitIcon className="size-4" /> Não é duplicado
          </Button>
        </div>
      </div>

      {suggestion ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-13 text-xs text-text-tertiary">
          {duplicateLeadIn(suggestion.matchReason, suggestion.suggestedRole)}
          <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-info-border bg-info-surface px-2 py-0.5 text-2xs font-semibold text-info-text">
            <span className="max-w-48 truncate">
              {suggestion.existing.institution ?? suggestion.existing.name}
            </span>
            <span className="font-mono tabular-nums">
              <MoneyValue text={formatCurrency(suggestion.existing.balance)} />
            </span>
          </span>
          {evidence ? <span>· {evidence}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function RefundReviewRow({
  item,
  busy,
  onConfirmRefund,
  onAlterRefund,
  onKeepAsExpense,
  onDismissRefund,
}: {
  item: ReviewQueueItem;
  busy: boolean;
  onConfirmRefund?: (item: ReviewQueueItem) => void;
  onAlterRefund?: (item: ReviewQueueItem) => void;
  onKeepAsExpense?: (item: ReviewQueueItem) => void;
  onDismissRefund?: (item: ReviewQueueItem) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const notArrived = item.reason === 'refund_not_arrived';
  const date = new Date(item.date);
  const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  const suggestion = item.refundSuggestion ?? null;

  const handleConfirm = () => onConfirmRefund?.(item);
  const handleAlter = () => onAlterRefund?.(item);
  const handleKeep = () => onKeepAsExpense?.(item);
  const handleDismiss = () => onDismissRefund?.(item);

  const TileIcon = notArrived ? ClockCountdownIcon : ArrowUUpLeftIcon;

  return (
    <div className="px-4 py-3 sm:px-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Spacer keeps the columns aligned with selectable rows; refund rows are
            excluded from batch confirms, so there is nothing to tick. */}
        <span className="size-4 shrink-0" aria-hidden />
        <span
          className={cn(
            'grid size-9 shrink-0 place-items-center rounded-lg border',
            notArrived
              ? 'border-warning-border bg-warning-surface text-warning-text'
              : 'border-info-border bg-info-surface text-info-text',
          )}
        >
          <TileIcon className="size-4.5" weight="duotone" />
        </span>
        <div className="min-w-0 flex-1 basis-40">
          <p className="truncate text-sm font-semibold text-text-primary">{item.description}</p>
          <p className="text-xs text-text-tertiary">
            {dateLabel}
            <span
              className={cn(
                'ml-2 inline-block rounded-full border px-1.5 py-px text-3xs font-medium whitespace-nowrap',
                notArrived
                  ? 'border-warning-border bg-warning-surface text-warning-text'
                  : 'border-info-border bg-info-surface text-info-text',
              )}
            >
              {REASON_LABEL[item.reason]}
            </span>
          </p>
        </div>
        <span className="font-mono text-sm font-medium text-text-primary tabular-nums">
          <MoneyValue text={formatCurrency(item.amount)} />
        </span>
        {/* On mobile the actions drop to their own full-width row and wrap (three of
            them — Confirmar / Alterar… / Não é reembolso — overflow a phone otherwise);
            from sm they sit inline and never shrink. */}
        <div className="flex w-full flex-wrap items-center gap-2 pl-13 sm:w-auto sm:flex-nowrap sm:shrink-0 sm:pl-0">
          {notArrived ? (
            <>
              <Button size="sm" disabled={busy || !onKeepAsExpense} onClick={handleKeep}>
                <CheckIcon className="size-4" /> Manter como despesa
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !onAlterRefund} onClick={handleAlter}>
                Vincular estorno…
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" disabled={busy || !suggestion || !onConfirmRefund} onClick={handleConfirm}>
                <CheckIcon className="size-4" /> Confirmar
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !onAlterRefund} onClick={handleAlter}>
                Alterar…
              </Button>
              <Button size="sm" variant="ghost" disabled={busy || !onDismissRefund} onClick={handleDismiss}>
                <ProhibitIcon className="size-4" /> Não é reembolso
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Proposal cluster — stacked below the main row so it stays visible on
          mobile, unlike the lg-only category-suggestion cluster. */}
      {notArrived ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-13 text-xs text-text-tertiary">
          Flag expirada
          <span className="inline-flex items-center gap-1 rounded-full border border-warning-border bg-warning-surface px-2 py-0.5 text-2xs font-semibold text-warning-text">
            <ReceiptIcon className="size-3" weight="bold" />
            {item.expiredRefund?.closedInvoiceLabel ?? 'prazo de 90 dias'}
          </span>
        </div>
      ) : suggestion ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-13 text-xs text-text-tertiary">
          Vincular a
          <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-info-border bg-info-surface px-2 py-0.5 text-2xs font-semibold text-info-text">
            <ArrowUUpLeftIcon className="size-3" weight="bold" />
            <span className="max-w-48 truncate">
              {suggestion.merchantName ?? suggestion.description}
            </span>
            <span className="font-mono tabular-nums">
              <MoneyValue text={formatCurrency(suggestion.amount)} />
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
}

function ReviewQueueRow({
  item,
  busy,
  selected,
  onToggleSelected,
  onConfirm,
  onAlter,
}: {
  item: ReviewQueueItem;
  busy: boolean;
  selected: boolean;
  onToggleSelected: () => void;
  onConfirm?: ReviewQueueCardProps['onConfirm'];
  onAlter?: (item: ReviewQueueItem) => void;
}) {
  const formatCurrency = useFormatCurrency();
  // Case (c) comes pre-checked: one-tap Confirmar creates the rule and ends the loop at
  // the second occurrence (spec §4). Unchecking keeps it one-off.
  const [createRule, setCreateRule] = useState(item.reason === 'user_categorized_before');
  const [applyToMerchant, setApplyToMerchant] = useState(false);

  // The row is the one-tap path, so it states the anchor rather than offering the choice:
  // the recommended one, named in the checkbox copy. "Alterar…" opens the drawer, which is
  // where the anchor can actually be changed — a radio pair per row in a dense list buys
  // an option almost nobody uses at the cost of every row.
  const anchor = buildRuleAnchors(item)[0];
  const date = new Date(item.date);
  const dateLabel = date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  const suggestionLabel =
    item.reason === 'user_categorized_before' ? 'Sua escolha anterior' : 'Sugerido';
  // Category rows always carry a suggestion; the fallback only shields against a
  // malformed row (refund rows render through RefundReviewRow instead).
  const suggestion = item.suggestion ?? {
    categoryRef: UNCATEGORIZED_CATEGORY_ID,
    source: 'pluggy' as const,
    name: 'Outros',
    icon: null,
  };
  // "Outros" as the fallback suggestion means there is no category to anchor a rule to —
  // the backend rejects createRule/applyToMerchant for these; direct users to "Alterar…".
  const canRule = suggestion.categoryRef !== UNCATEGORIZED_CATEGORY_ID;
  const visual = categoryVisual(suggestion.categoryRef, suggestion.source);

  return (
    <div className={cn('px-4 py-3 sm:px-5', selected && 'bg-brand-100/30')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <input
          type="checkbox"
          aria-label={`Selecionar ${item.description}`}
          className="size-4 shrink-0 accent-brand-primary"
          checked={selected}
          disabled={busy}
          onChange={onToggleSelected}
        />
        <span
          className="grid size-9 shrink-0 place-items-center rounded-lg"
          style={{ backgroundColor: visual.backgroundColor, color: visual.color }}
        >
          <CategoryIcon name={suggestion.icon} className="size-4.5" />
        </span>
        <div className="min-w-0 flex-1 basis-40">
          <p className="truncate text-sm font-semibold text-text-primary">{item.description}</p>
          <p className="text-xs text-text-tertiary">
            {dateLabel}
            <span className="ml-2 rounded-full bg-muted px-1.5 py-px text-3xs font-medium text-text-secondary">
              {REASON_LABEL[item.reason]}
            </span>
          </p>
        </div>
        <span className="hidden items-center gap-1.5 text-xs text-text-tertiary lg:flex">
          {suggestionLabel}
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold"
            style={{ backgroundColor: visual.backgroundColor, color: visual.color }}
          >
            <span className="size-1.5 rounded-full" style={{ backgroundColor: visual.accent }} aria-hidden />
            {suggestion.name}
          </span>
        </span>
        <span className="font-mono text-sm font-medium text-text-primary tabular-nums">
          <MoneyValue text={formatCurrency(item.amount)} />
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="sm"
            disabled={busy || !onConfirm}
            onClick={() => onConfirm?.(item, { createRule, applyToMerchant, ruleAnchor: anchor.kind })}
          >
            <CheckIcon className="size-4" /> Confirmar
          </Button>
          <Button size="sm" variant="outline" disabled={busy || !onAlter} onClick={() => onAlter?.(item)}>
            Alterar…
          </Button>
        </div>
      </div>

      {canRule && (
        <RuleCreationFields
          className="mt-2 pl-13"
          anchor={anchor}
          categoryName={suggestion.name}
          value={{ createRule, applyToMerchant }}
          onChange={(next) => {
            setCreateRule(next.createRule);
            setApplyToMerchant(next.applyToMerchant);
          }}
          disabled={busy}
        />
      )}
    </div>
  );
}
